// infrastructure/mappers/serviceDeliveryMapper.js
import { createServiceDelivery, placeOfServiceIdOf, treatmentServiceIdsOf } from '../../domain/model/ServiceDelivery';

/**
 * Where the stored document and the domain value meet.
 *
 * The two shapes are the same shape here, deliberately: `serviceDelivery.serviceEntries` is already
 * the vocabulary the domain uses, so this mapper renames nothing. What it does instead is guarantee
 * the invariants the document cannot — that entries are an array, that the value is frozen, and that
 * a field the document happens to carry does not become a field of the domain object.
 *
 * IT ALSO ANSWERS WHICH STORED RECORDS MAY CARRY ONE. That question is asked in the mud's terms —
 * `sessionActivities` conflates staff time, clinical delivery and administrative artifacts under one
 * `type` — so it is answered here rather than in `domain/`, which names no legacy vocabulary. The
 * value object itself says nothing about activity types; it is constrained by them, not defined by
 * them.
 *
 * NULL AND EMPTY ARE DIFFERENT AND BOTH SURVIVE THE ROUND TRIP. `null` means the record has no
 * service delivery — a non-billable activity, or a plan that predates the feature. An empty delivery
 * means the record has one and nothing has been chosen yet. The activity editor already relies on
 * that distinction to decide whether saving should clear a stored value, so collapsing the two here
 * would silently rewrite records the user never touched.
 */

/**
 * The plan field declaring the service delivery for each stored activity type that delivers one.
 *
 * Not exported: callers ask a function rather than holding the map, so nobody grows a second opinion
 * about what counts or about which field answers for what. A break or a drive records time without
 * delivering anything, and a form is an artifact rather than an encounter.
 *
 * THE LIST IS CLOSED ON PURPOSE, and it is a domain boundary rather than an oversight. Session and
 * Supervision are the two things that deliver a service to a client. A clinic can author its own
 * activity types in settings, and those are staff time or an artifact sharing the same collection —
 * the conflation this layer exists to translate, not a service delivery waiting for a field. Adding
 * one here would mean deciding that a clinic-named category delivers clinical care, which is not a
 * decision this layer gets to make.
 *
 * The plan qualifies each field because it declares one per activity type it produces. The activity
 * does not, because it records the one it delivered.
 */
const PLAN_DECLARATION_FIELDS = {
  session: 'sessionServiceDelivery',
  supervision: 'supervisionServiceDelivery',
};

/**
 * Whether a stored activity of this type may carry a service delivery.
 *
 * Deliberately NOT a question about billing: whether a delivered service is then billed is decided
 * downstream, at the date of service.
 *
 * @param {string} activityType
 * @returns {boolean}
 */
export function canCarryServiceDelivery(activityType) {
  return Object.hasOwn(PLAN_DECLARATION_FIELDS, activityType);
}

/**
 * @param {Object|null|undefined} document - the stored `serviceDelivery` field
 * @returns {import('../../domain/model/ServiceDelivery').ServiceDelivery|null}
 */
export function toServiceDelivery(document) {
  if (document === null || document === undefined) return null;

  return createServiceDelivery({
    placeOfServiceId: document.placeOfServiceId ?? null,
    // Guarded rather than defaulted: `?? []` still hands a non-list to `.map()`, and a stored shape
    // nobody expected should not stop the editor opening. Deduplication is the value object's.
    treatmentServiceIds: (Array.isArray(document.serviceEntries) ? document.serviceEntries : [])
      .map((entry) => entry?.treatmentServiceId)
      .filter(Boolean),
  });
}

/**
 * The service delivery a plan declared for the activity type about to be created, or null.
 *
 * ONE DECLARATION PER ACTIVITY TYPE, and choosing between them is the whole point. Direct therapy
 * and supervision are different services; giving a supervision activity the session's is not a
 * smaller version of the right answer, it is the wrong service against the wrong work.
 *
 * Null both when the type delivers no service and when the plan declared nothing for it, because a
 * caller does the same thing in either case: leave the activity's field absent, so a plan that
 * declares only one of the two behaves for the other exactly as it did before.
 *
 * @param {Object|null|undefined} plan - the stored session plan
 * @param {string} activityType
 * @returns {import('../../domain/model/ServiceDelivery').ServiceDelivery|null}
 */
export function toDeclaredServiceDelivery(plan, activityType) {
  const field = PLAN_DECLARATION_FIELDS[activityType];
  if (field === undefined) return null;

  return toServiceDelivery(plan?.[field]);
}

/**
 * @param {import('../../domain/model/ServiceDelivery').ServiceDelivery|null|undefined} delivery
 * @returns {Object|null} the shape to store
 */
export function toServiceDeliveryDocument(delivery) {
  if (delivery === null || delivery === undefined) return null;

  return {
    placeOfServiceId: placeOfServiceIdOf(delivery),
    serviceEntries: treatmentServiceIdsOf(delivery)
      .map((treatmentServiceId) => ({ treatmentServiceId })),
  };
}

/**
 * The fields a session plan stores to declare `delivery` for one activity type: the write side of
 * `toDeclaredServiceDelivery`, so a caller outside never names the plan's fields.
 *
 * @param {string} activityType
 * @param {import('../../domain/model/ServiceDelivery').ServiceDelivery} delivery
 * @returns {Object|null} null for a type a plan declares nothing for
 */
export function toServiceDeliveryDeclaration(activityType, delivery) {
  const field = PLAN_DECLARATION_FIELDS[activityType];
  if (field === undefined) return null;

  return { [field]: toServiceDeliveryDocument(delivery) };
}
